import { gql } from '@apollo/client'
import { apolloClient } from '../apollo/client'
import { Placar, SetPlacar } from '../types/placar'

/**
 * Query para buscar partidas com filtros.
 * 
 * Nota sobre estrutura de endereço: A query retorna cidade.estado.uf (estrutura aninhada),
 * que está alinhada com outras queries do projeto (PARTIDAS_POR_ATLETA, PARTIDA_POR_ID).
 * A interface TypeScript PartidaPorTorneio reflete essa estrutura corretamente.
 */
export const PARTIDAS_COM_FILTROS = gql`
  query PartidasComFiltros($filtros: PartidaFiltroInput!) {
    partidasComFiltros(filtros: $filtros) {
      id
      numeroPartida
      data
      status
      numeroSets
      gamesPorSets
      tiebreaks
      faseCategoriaTorneio {
        id
        nome
        ordem
      }
      categoriaTorneio {
        id
        nome
        numeroSets
        gamesPorSets
        tiebreaks
        torneio { id nome avatarUrl }
      }
      faseGrupoPartidas {
        id
        nome
      }
      local {
        id
        nome
        urlLocalizacao
        endereco {
          id
          logradouro
          numero
          complemento
          bairro
          cidade {
            id
            nome
            estado {
              id
              uf
            }
          }
          cep
        }
      }
      equipeA {
        id
        nome
        atletas {
          id
          usuario {
            id
            nome
            imagemUrl
            username
          }
        }
      }
      equipeB {
        id
        nome
        atletas {
          id
          usuario {
            id
            nome
            imagemUrl
            username
          }
        }
      }
      participantesA {
        id
        usuario {
          id
          nome
          imagemUrl
          username
        }
      }
      participantesB {
        id
        usuario {
          id
          nome
          imagemUrl
          username
        }
      }
      placar {
        id
        resultadoFinalA
        resultadoFinalB
        sets {
          gamesA
          gamesB
          numeroSet
          tiebreakA
          tiebreakB
        }
      }
      equipeAWO
      equipeBWO
      equipeADesistencia
      equipeBDesistencia
      equipeVencedora
      criadoEm
      atualizadoEm
    }
  }
`

/**
 * Query legada para buscar todas as partidas de um torneio.
 * @deprecated Use PARTIDAS_COM_FILTROS com filtro torneioId
 */
export const PARTIDAS_POR_TORNEIO = gql`
  query PartidasPorTorneio($torneioId: ID!) {
    partidasPorTorneio(torneioId: $torneioId) {
      id
      numeroPartida
      data
      status
      numeroSets
      gamesPorSets
      tiebreaks
      faseCategoriaTorneio {
        id
        nome
        ordem
      }
      categoriaTorneio {
        id
        nome
        numeroSets
        gamesPorSets
        tiebreaks
      }
      faseGrupoPartidas {
        id
        nome
      }
      local {
        id
        nome
        endereco {
          id
          logradouro
          numero
          complemento
          bairro
          cidade {
            id
            nome
            estado {
              id
              uf
            }
          }
          cep
        }
      }
      equipeA {
        id
        nome
        atletas {
          id
          usuario {
            id
            nome
            imagemUrl
            username
          }
        }
      }
      equipeB {
        id
        nome
        atletas {
          id
          usuario {
            id
            nome
            imagemUrl
            username
          }
        }
      }
      placar {
        id
        resultadoFinalA
        resultadoFinalB
        sets {
          gamesA
          gamesB
          numeroSet
          tiebreakA
          tiebreakB
        }
      }
      equipeAWO
      equipeBWO
      equipeADesistencia
      equipeBDesistencia
      equipeVencedora
    }
  }
`

export const PARTIDAS_POR_ATLETA = gql`
  query PartidaPorAtleta($id: ID!) {
    partidaPorAtleta(atletaId: $id) {
      ID
      atualizadoEm
      criadoEm
      data
      faseCategoriaTorneio {
        id
        nome
        ordem
      }
      categoriaTorneio {
        id
        nome
        quantidadeFases
        torneio { id nome avatarUrl }
        numeroAtletas
        aplicarDescontoTorneio
        numeroSets
        gamesPorSets
        tiebreaks
      }
      equipeA {
        id
        nome
        atletas { id usuario { id nome imagemUrl username } }
      }
      equipeB {
        id
        nome
        atletas { id usuario { id nome imagemUrl username } }
      }
      participantesA { id usuario { id nome imagemUrl username } }
      participantesB { id usuario { id nome imagemUrl username } }
      local {
        id
        nome
        urlLocalizacao
        endereco {
          bairro
          cep
          complemento
          id
          logradouro
          numero
          principal
          cidade { id nome estado { id nome uf } }
        }
      }
      placar {
        id
        resultadoFinalA
        resultadoFinalB
        sets {
          gamesA
          gamesB
          numeroSet
          tiebreakA
          tiebreakB
        }
      }
      equipeAWO
      equipeBWO
      equipeADesistencia
      equipeBDesistencia
      equipeVencedora
      status
    }
  }
`

export interface PartidaPorTorneio {
  id: string
  numeroPartida: number | null
  data: string | null
  status: 'AGENDADA' | 'EM_ANDAMENTO' | 'FINALIZADA' | 'CANCELADA'
  criadoEm?: string | null
  atualizadoEm?: string | null
  equipeVencedora?: string | null
  equipeAWO?: boolean | null
  equipeBWO?: boolean | null
  equipeADesistencia?: boolean | null
  equipeBDesistencia?: boolean | null
  numeroSets?: number | null
  gamesPorSets?: number | null
  tiebreaks?: number | null
  faseCategoriaTorneio: {
    id: string
    nome: string
    ordem: number
  } | null
  categoriaTorneio: {
    id: string
    nome: string
    numeroSets?: number | null
    gamesPorSets?: number | null
    tiebreaks?: number | null
    torneio?: {
      id: string
      nome: string
      avatarUrl?: string | null
    }
  }
  faseGrupoPartidas: {
    id: string
    nome: string
  } | null
  local: {
    id: string
    nome: string
    urlLocalizacao?: string | null
    endereco: {
      id: string
      logradouro: string
      numero: string
      complemento: string | null
      bairro: string
      cidade: {
        id: string
        nome: string
        estado: {
          id: string
          uf: string
        }
      }
      cep: string
    } | null
  } | null
  equipeA: {
    id: string
    nome: string
    atletas: {
      id: string
      usuario: {
        id: string
        nome: string
        imagemUrl: string | null
        username: string
      }
    }[]
  } | null
  equipeB: {
    id: string
    nome: string
    atletas: {
      id: string
      usuario: {
        id: string
        nome: string
        imagemUrl: string | null
        username: string
      }
    }[]
  } | null
  participantesA?: {
    id: string
    usuario?: {
      id: string
      nome: string
      imagemUrl: string | null
      username: string
    } | null
  }[] | null
  participantesB?: {
    id: string
    usuario?: {
      id: string
      nome: string
      imagemUrl: string | null
      username: string
    } | null
  }[] | null
  placar: {
    id: string
    resultadoFinalA: number | null
    resultadoFinalB: number | null
    sets: {
      gamesA: number | null
      gamesB: number | null
      numeroSet: number
      tiebreakA: number | null
      tiebreakB: number | null
    }[]
  } | null
}

export interface PartidasPorTorneioData {
  partidasPorTorneio: PartidaPorTorneio[]
}

export interface PartidasPorTorneioVars {
  torneioId: string
}

export interface PartidaFiltroInput {
  torneioId?: string | null
  categoriaTorneioId?: string | null
  atletaId?: string | null
  dataInicio?: string | null
  dataFim?: string | null
  status?: string | null
}

export interface PartidasComFiltrosData {
  partidasComFiltros: PartidaPorTorneio[]
}

export interface PartidasComFiltrosVars {
  filtros: PartidaFiltroInput
}

export interface Partida {
  id: string
  numeroPartida: number
  data: string
  status: 'AGENDADA' | 'EM_ANDAMENTO' | 'FINALIZADA' | 'CANCELADA'
  criadoEm: string
  atualizadoEm: string
  equipeVencedora?: string | null
  equipeAWO?: boolean | null
  equipeBWO?: boolean | null
  equipeADesistencia?: boolean | null
  equipeBDesistencia?: boolean | null
  numeroSets?: number | null
  gamesPorSets?: number | null
  tiebreaks?: number | null
  categoriaTorneio: {
    id: string
    nome: string
    numeroSets?: number | null
    gamesPorSets?: number | null
    tiebreaks?: number | null
    numeroAtletas: number
    quantidadeFases: number
    aplicarDescontoTorneio?: boolean | null
    torneio: { id: string; nome: string; avatarUrl?: string | null }
  }
  faseCategoriaTorneio: {
    id: string
    nome: string
    ordem: number
  } | null
  faseGrupoPartidas: {
    id: string
    nome: string
  } | null
  equipeA: {
    id: string
    nome: string
    atletas: { id: string; usuario: { id: string; nome: string; username: string; imagemUrl: string | null } }[]
  } | null
  equipeB: {
    id: string
    nome: string
    atletas: { id: string; usuario: { id: string; nome: string; username: string; imagemUrl: string | null } }[]
  } | null
  participantesA?: { id: string; usuario?: { id: string; nome: string; username: string; imagemUrl: string | null } | null }[] | null
  participantesB?: { id: string; usuario?: { id: string; nome: string; username: string; imagemUrl: string | null } | null }[] | null
  local: {
    id: string
    nome: string
    urlLocalizacao?: string | null
    endereco: {
      id: string
      logradouro: string
      numero: string
      complemento: string
      bairro: string
      cep: string
      principal: boolean
      cidade: {
        id: string
        nome: string
        estado: { id: string; nome: string; uf: string }
      }
    }
  } | null
  placar: (Placar & { id: string }) | null
}

export type SetPlacarInput = SetPlacar;

export interface PlacarInput {
  resultadoFinalA?: number | null;
  resultadoFinalB?: number | null;
  sets: SetPlacarInput[];
}

export interface PartidaInput {
  categoriaTorneioId: string
  faseCategoriaTorneioId?: string | null
  faseGrupoPartidasId?: string | null
  equipeAId?: string | null
  equipeBId?: string | null
  participantesAIds?: (string | null)[] | null
  participantesBIds?: (string | null)[] | null
  numeroPartida?: number | null
  data?: string | null
  localId?: string | null
  status?: string | null
  placar?: PlacarInput | null
  equipeVencedora?: string | null
  equipeAWO?: boolean | null
  equipeBWO?: boolean | null
  equipeADesistencia?: boolean | null
  equipeBDesistencia?: boolean | null
  numeroSets?: number | null
  gamesPorSets?: number | null
  tiebreaks?: number | null
}

import { calculateFinalResults } from '../types/placar';

function mapPlacarInputToApi(placar?: PlacarInput | null) {
  if (!placar) {
    return null;
  }

  const { resultadoFinalA, resultadoFinalB } = calculateFinalResults(placar.sets);

  return {
    resultadoFinalA,
    resultadoFinalB,
    sets: (placar.sets ?? []).map(set => ({
      gamesA: set.gamesA ?? null,
      gamesB: set.gamesB ?? null,
      numeroSet: set.numeroSet,
      tiebreakA: set.tiebreakA ?? null,
      tiebreakB: set.tiebreakB ?? null,
    })),
  };
}
export const partidasPorAtleta = async (id: string) => {
  const { data } = await apolloClient.query<{ partidaPorAtleta: Partida[] }, { id: string }>({
    query: PARTIDAS_POR_ATLETA,
    variables: { id },
    fetchPolicy: 'no-cache',
  })
  return data.partidaPorAtleta
}

export const partidasPorTorneio = async (torneioId: string) => {
  const { data } = await apolloClient.query<PartidasPorTorneioData, PartidasPorTorneioVars>({
    query: PARTIDAS_POR_TORNEIO,
    variables: { torneioId },
    fetchPolicy: 'no-cache',
  })
  return data.partidasPorTorneio
}

export const partidasComFiltros = async (filtros: PartidaFiltroInput) => {
  const { data } = await apolloClient.query<PartidasComFiltrosData, PartidasComFiltrosVars>({
    query: PARTIDAS_COM_FILTROS,
    variables: { filtros },
    fetchPolicy: 'no-cache',
  })
  return data.partidasComFiltros
}

export const PARTIDA_POR_ID = gql`
  query PartidaPorId($id: ID!) {
    partidaPorId(id: $id) {
      id
      atualizadoEm
      criadoEm
      data
      numeroSets
      gamesPorSets
      tiebreaks
      categoriaTorneio {
        id
        nome
        aplicarDescontoTorneio
        numeroSets
        gamesPorSets
        tiebreaks
        torneio { id nome avatarUrl }
      }
      equipeA {
        id
        nome
        atletas { id usuario { id nome imagemUrl username } }
      }
      equipeB {
        id
        nome
        atletas { id usuario { id nome imagemUrl username } }
      }
      participantesA { id usuario { id nome imagemUrl username } }
      participantesB { id usuario { id nome imagemUrl username } }
      local {
        id
        nome
        urlLocalizacao
        endereco {
          bairro
          cep
          complemento
          id
          logradouro
          numero
          principal
          cidade { id nome estado { id nome uf } }
        }
      }
      placar {
        id
        resultadoFinalA
        resultadoFinalB
        sets {
          gamesA
          gamesB
          numeroSet
          tiebreakA
          tiebreakB
        }
      }
      equipeAWO
      equipeBWO
      equipeADesistencia
      equipeBDesistencia
      equipeVencedora
      status
    }
  }
`

export interface PartidaPorIdData {
  partidaPorId: Partida
}

export interface PartidaPorIdVars {
  id: string
}

export const partidaPorId = async (id: string) => {
  const { data } = await apolloClient.query<PartidaPorIdData, PartidaPorIdVars>({
    query: PARTIDA_POR_ID,
    variables: { id },
    fetchPolicy: 'no-cache',
  })
  return data.partidaPorId
}

export const CRIAR_PARTIDA = gql`
  mutation CriarPartida($input: PartidaInput!) {
    criarPartida(input: $input) {
      id
      numeroPartida
    }
  }
`

export const ATUALIZAR_PARTIDA = gql`
  mutation AtualizarPartida($id: ID!, $input: PartidaInput!) {
    atualizarPartida(id: $id, input: $input) {
      id
      numeroPartida
      placar {
        id
        resultadoFinalA
        resultadoFinalB
        sets {
          gamesA
          gamesB
          numeroSet
          tiebreakA
          tiebreakB
        }
      }
    }
  }
`

export const DELETAR_PARTIDA = gql`
  mutation DeletarPartida($id: ID!) {
    deletarPartida(id: $id)
  }
`

export const GERAR_PARTIDAS_CATEGORIA = gql`
  mutation GerarPartidasCategoria($categoriaTorneioId: ID!) {
    gerarPartidasCategoria(categoriaTorneioId: $categoriaTorneioId) {
      id
      numeroPartida
      equipeA { nome }
      equipeB { nome }
      faseCategoriaTorneio { nome }
      faseGrupoPartidas { nome }
    }
  }
`

export async function criarPartida(input: {
  faseGrupoPartidasId?: string;
  [key: string]: any;
}): Promise<{ id: string }> {
  const { data } = await apolloClient.mutate({
    mutation: CRIAR_PARTIDA,
    variables: { input },
  })
  return data?.criarPartida
}

export async function atualizarPartida(id: string, input: {
  [key: string]: any;
}): Promise<{ id: string }> {
  const { data } = await apolloClient.mutate({
    mutation: ATUALIZAR_PARTIDA,
    variables: { id, input },
  })
  return data?.atualizarPartida
}

export async function deletarPartida(id: string): Promise<boolean> {
  const { data } = await apolloClient.mutate({
    mutation: DELETAR_PARTIDA,
    variables: { id },
  })
  return data?.deletarPartida
}

export async function gerarPartidasCategoria(categoriaTorneioId: string) {
  const { data } = await apolloClient.mutate({
    mutation: GERAR_PARTIDAS_CATEGORIA,
    variables: { categoriaTorneioId },
  })
  return data?.gerarPartidasCategoria
}

export const ATUALIZAR_CONFIGURACOES_PARTIDA = gql`
  mutation AtualizarConfiguracoesPartida($id: ID!, $numeroSets: Int, $gamesPorSets: Int, $tiebreaks: Int) {
    atualizarConfiguracoesPartida(id: $id, numeroSets: $numeroSets, gamesPorSets: $gamesPorSets, tiebreaks: $tiebreaks) {
      id
      numeroSets
      gamesPorSets
      tiebreaks
    }
  }
`

export interface AtualizarConfiguracoesPartidaVars {
  id: string
  numeroSets?: number | null
  gamesPorSets?: number | null
  tiebreaks?: number | null
}

export interface AtualizarConfiguracoesPartidaData {
  atualizarConfiguracoesPartida: {
    id: string
    numeroSets?: number | null
    gamesPorSets?: number | null
    tiebreaks?: number | null
  }
}

export async function atualizarConfiguracoesPartida(
  id: string,
  config: {
    numeroSets?: number | null
    gamesPorSets?: number | null
    tiebreaks?: number | null
  }
): Promise<{ id: string; numeroSets?: number | null; gamesPorSets?: number | null; tiebreaks?: number | null }> {
  const { data } = await apolloClient.mutate<AtualizarConfiguracoesPartidaData, AtualizarConfiguracoesPartidaVars>({
    mutation: ATUALIZAR_CONFIGURACOES_PARTIDA,
    variables: { id, ...config },
  })
  if (!data?.atualizarConfiguracoesPartida) {
    throw new Error('Erro ao atualizar configurações da partida')
  }
  return data.atualizarConfiguracoesPartida
}

// --------------------------------------------------------------------------------
// Query paginada: partidas do atleta
// --------------------------------------------------------------------------------

export interface PartidaResumo {
  id: string
  data: string | null
  status: 'AGENDADA' | 'EM_ANDAMENTO' | 'FINALIZADA' | 'CANCELADA'
  categoriaTorneio: {
    id: string
    nome: string
    torneio: {
      id: string
      nome: string
      avatarUrl?: string | null
    }
  }
  equipeA: {
    id: string
    nome: string
    atletas: {
      id: string
      usuario: {
        id: string
        nome: string
        imagemUrl: string | null
        username: string
      }
    }[]
  } | null
  equipeB: {
    id: string
    nome: string
    atletas: {
      id: string
      usuario: {
        id: string
        nome: string
        imagemUrl: string | null
        username: string
      }
    }[]
  } | null
  participantesA?: {
    id: string
    usuario?: {
      id: string
      nome: string
      imagemUrl: string | null
      username: string
    } | null
  }[] | null
  participantesB?: {
    id: string
    usuario?: {
      id: string
      nome: string
      imagemUrl: string | null
      username: string
    } | null
  }[] | null
  placar: {
    id: string
    resultadoFinalA: number | null
    resultadoFinalB: number | null
    sets: {
      gamesA: number | null
      gamesB: number | null
      numeroSet: number
      tiebreakA: number | null
      tiebreakB: number | null
    }[]
  } | null
  equipeAWO?: boolean | null
  equipeBWO?: boolean | null
  equipeADesistencia?: boolean | null
  equipeBDesistencia?: boolean | null
  equipeVencedora?: string | null
}

export const PARTIDAS_POR_ATLETA_PAGINADO = gql`
  query PartidasPorAtletaPaginado($atletaId: ID!, $page: Int!, $size: Int!) {
    partidasPorAtletaPaginado(
      atletaId: $atletaId
      page: $page
      size: $size
      sortBy: "data"
      direction: "DESC"
    ) {
      page
      size
      totalItems
      totalPages
      items {
        id
        data
        status
        categoriaTorneio {
          id
          nome
          torneio {
            id
            nome
            avatarUrl
          }
        }
        equipeA {
          id
          nome
          atletas {
            id
            usuario {
              id
              nome
              imagemUrl
              username
            }
          }
        }
        equipeB {
          id
          nome
          atletas {
            id
            usuario {
              id
              nome
              imagemUrl
              username
            }
          }
        }
        placar {
          id
          resultadoFinalA
          resultadoFinalB
          sets {
            gamesA
            gamesB
            numeroSet
            tiebreakA
            tiebreakB
          }
        }
        equipeAWO
        equipeBWO
        equipeADesistencia
        equipeBDesistencia
        equipeVencedora
      }
    }
  }
`

export interface PartidasPorAtletaPaginadoData {
  partidasPorAtletaPaginado: {
    page: number
    size: number
    totalItems: number
    totalPages: number
    items: PartidaResumo[]
  }
}

export interface PartidasPorAtletaPaginadoVars {
  atletaId: string
  page: number
  size: number
}

export const listarPartidasPorAtletaPaginado = async (
  atletaId: string,
  page: number,
  size: number
) => {
  const { data } = await apolloClient.query<
    PartidasPorAtletaPaginadoData,
    PartidasPorAtletaPaginadoVars
  >({
    query: PARTIDAS_POR_ATLETA_PAGINADO,
    variables: { atletaId, page, size },
    fetchPolicy: 'no-cache',
  })
  return data.partidasPorAtletaPaginado
}
